import type { CostReportClusterSummaryNormalized } from '@cast/shared-cost-data-access';
import { WorkloadReliabilitySummariesResponse } from '@cast/types';
import { dashesOrNum, toNumberWithSeparators } from '@cast/utils';
import type { ArrayValues } from 'type-fest';

import { formatLatency } from './formatLatency';

export type SloMetricStatus = 'ok' | 'breached' | 'at_risk' | 'locked';

export type SloMetric = {
  id: string;
  label: string;
  value: string;
  unit?: string;
  status: SloMetricStatus;
};

const UNHEALTHY_ERROR_RATE_THRESHOLD = 5;
const AT_RISK_ERROR_RATE_THRESHOLD = 2;
const LATENCY_BREACH_SECONDS = 0.5;
const LATENCY_RISK_SECONDS = 0.3;

/** Empty metric display — `@cast/utils` `dashesOrNum({ value: undefined })` → `--`. */
export const EMPTY_METRIC_VALUE = String(dashesOrNum({ value: undefined }));

const SLO_METRIC_ORDER = [
  'latency',
  'errRate',
  'reqRate',
  'oom',
  'restarts',
  'disrupt',
] as const;

// Dashboard service-health tiles. Hidden until APIs are wired (always empty today):
// nodeFailures, cpuSaturation, reqRate, restarts, disrupt.
export const SERVICE_HEALTH_METRIC_ORDER = [
  'nodeAvailability',
  'latency',
  'errRate',
  'oom',
] as const;

export type ServiceHealthMetricId = ArrayValues<
  typeof SERVICE_HEALTH_METRIC_ORDER
>;

/** Metrics that typically need agent / reliability collector. */
export const SERVICE_HEALTH_RED_METRIC_IDS = new Set<ServiceHealthMetricId>([
  'latency',
  'errRate',
  'oom',
]);

const orderSloMetrics = (metrics: SloMetric[]): SloMetric[] => {
  const byId = new Map(metrics.map((metric) => [metric.id, metric]));

  return SLO_METRIC_ORDER.flatMap((id) => {
    const metric = byId.get(id);
    return metric ? [metric] : [];
  });
};

const orderServiceHealthMetrics = (metrics: SloMetric[]): SloMetric[] => {
  const byId = new Map(metrics.map((metric) => [metric.id, metric]));

  return SERVICE_HEALTH_METRIC_ORDER.flatMap((id) => {
    const metric = byId.get(id);
    return metric ? [metric] : [];
  });
};

const formatLatencyMetric = (
  seconds?: number
): { value: string; unit?: string } => {
  if (seconds === undefined) {
    return { value: EMPTY_METRIC_VALUE };
  }

  const formatted = formatLatency(seconds * 1000, 1);

  if (!formatted) {
    return { value: EMPTY_METRIC_VALUE };
  }

  return {
    value: String(formatted.value),
    unit: formatted.unit,
  };
};

const formatErrorRateValue = (errorRatePct?: number): string => {
  if (errorRatePct === undefined) {
    return EMPTY_METRIC_VALUE;
  }

  return String(dashesOrNum({ value: errorRatePct, roundBy: 1 }));
};

const getLatencyStatus = (seconds?: number): SloMetricStatus => {
  if (seconds === undefined) {
    return 'ok';
  }

  if (seconds > LATENCY_BREACH_SECONDS) {
    return 'breached';
  }

  if (seconds > LATENCY_RISK_SECONDS) {
    return 'at_risk';
  }

  return 'ok';
};

const getErrorRateStatus = (errorRatePct?: number): SloMetricStatus => {
  if (errorRatePct === undefined) {
    return 'ok';
  }

  if (errorRatePct > UNHEALTHY_ERROR_RATE_THRESHOLD) {
    return 'breached';
  }

  if (errorRatePct > AT_RISK_ERROR_RATE_THRESHOLD) {
    return 'at_risk';
  }

  return 'ok';
};

type ClusterNodeCounts = Pick<
  CostReportClusterSummaryNormalized,
  'nodesCountTotal' | 'nodesCountManagedTotal'
>;

const buildClusterHealthMetricsFromSummary = (
  summary?: ClusterNodeCounts
): SloMetric[] => {
  const totalNodes = summary?.nodesCountTotal.toNumber() ?? 0;
  const managedNodes = summary?.nodesCountManagedTotal.toNumber() ?? 0;
  const nodeAvailabilityPct =
    totalNodes > 0 ? Math.round((managedNodes / totalNodes) * 100) : null;

  return [
    {
      id: 'nodeAvailability',
      label: 'Node availability',
      value:
        nodeAvailabilityPct !== null
          ? String(nodeAvailabilityPct)
          : EMPTY_METRIC_VALUE,
      unit: nodeAvailabilityPct !== null ? '%' : undefined,
      status: 'ok',
    },
    // Hidden until API is wired: nodeFailures, cpuSaturation
  ];
};

export const buildSloMetricsFromReliability = (
  data?: WorkloadReliabilitySummariesResponse,
  oomCount?: number
): SloMetric[] => {
  const latencyMetric = data?.highestLatencyP95?.[0]?.metric;
  const errorRatePct = data?.highestErrorRatePercent?.[0]?.metric;

  const oomBreached = (oomCount ?? 0) > 0;

  const metrics: SloMetric[] = [
    {
      id: 'latency',
      label: 'Latency',
      ...formatLatencyMetric(latencyMetric),
      status: getLatencyStatus(latencyMetric),
    },
    {
      id: 'errRate',
      label: 'Error rates',
      // Value and "%" split so unit stays secondary (like Node availability).
      value: formatErrorRateValue(errorRatePct),
      unit: errorRatePct !== undefined ? '%' : undefined,
      status: getErrorRateStatus(errorRatePct),
    },
    // Kept for clusters-list SLO columns; omitted from dashboard via SERVICE_HEALTH_METRIC_ORDER.
    // Hidden on dashboard until API is wired: reqRate, restarts, disrupt.
    {
      id: 'reqRate',
      label: 'Request rate',
      value: EMPTY_METRIC_VALUE,
      status: 'ok',
    },
    {
      id: 'oom',
      label: 'OOMKills',
      value:
        oomCount !== undefined
          ? toNumberWithSeparators(oomCount)
          : EMPTY_METRIC_VALUE,
      status: oomBreached ? 'breached' : 'ok',
    },
    {
      id: 'restarts',
      label: 'Restarts',
      value: EMPTY_METRIC_VALUE,
      status: 'ok',
    },
    {
      id: 'disrupt',
      label: 'Disruptions',
      value: EMPTY_METRIC_VALUE,
      status: 'ok',
    },
  ];

  return orderSloMetrics(metrics);
};

export const buildServiceHealthMetrics = ({
  reliability,
  summary,
  oomCount,
}: {
  reliability?: WorkloadReliabilitySummariesResponse;
  summary?: ClusterNodeCounts;
  oomCount?: number;
}): { metrics: SloMetric[]; breachCount: number; riskCount: number } => {
  const reliabilityMetrics = buildSloMetricsFromReliability(
    reliability,
    oomCount
  );
  const clusterMetrics = buildClusterHealthMetricsFromSummary(summary);
  const metrics = orderServiceHealthMetrics([
    ...clusterMetrics,
    ...reliabilityMetrics,
  ]);

  return {
    metrics,
    breachCount: metrics.filter((metric) => metric.status === 'breached')
      .length,
    riskCount: metrics.filter((metric) => metric.status === 'at_risk').length,
  };
};

export const isEmptyMetricValue = (value?: string) =>
  !value || value === EMPTY_METRIC_VALUE || value === '—';
